import React, { useEffect, useMemo, useState } from 'react';
import {
  Chart as ChartJS, CategoryScale, LinearScale, BarElement, PointElement, LineElement, Tooltip, Legend
} from 'chart.js';
import { Bar, Line } from 'react-chartjs-2';
import { getKPIAnalytics } from '../../services/api';
import { fmtScore, fmtValue, money, MONTHS_SHORT, scoreTone } from './kpiFormat';

ChartJS.register(CategoryScale, LinearScale, BarElement, PointElement, LineElement, Tooltip, Legend);

/* Цвета графиков — только токены дизайн-системы (проверенная палитра
   --c-chart-*), читаем их из CSS, а не хардкодим. */
const token = (name) => getComputedStyle(document.documentElement).getPropertyValue(name).trim();

const axisGrid = () => ({ color: token('--c-border') });

const KpiAnalytics = ({ data }) => {
  const [series, setSeries] = useState(null);
  const [error, setError] = useState(null);
  const [metric, setMetric] = useState('');

  useEffect(() => {
    let alive = true;
    getKPIAnalytics(12)
      .then(r => { if (alive) setSeries(r.months); })
      .catch(() => { if (alive) setError('Не удалось загрузить аналитику'); });
    return () => { alive = false; };
  }, [data]);

  const roleName = useMemo(() => Object.fromEntries(data.roles.map(r => [r.key, r.name])), [data.roles]);
  const metricInfo = useMemo(() => {
    const out = {};
    data.roles.forEach(r => r.metrics.forEach(m => { out[`${r.key}.${m.key}`] = { ...m, role: r.key, roleName: r.name }; }));
    return out;
  }, [data.roles]);

  // Слабые места текущего месяца: средний балл показателя по должности.
  const weak = useMemo(() => {
    const acc = {};
    data.employees.forEach(e => e.result.rows.forEach(row => {
      if (row.score === null) return;
      const k = `${e.role}.${row.key}`;
      (acc[k] = acc[k] || []).push(row.score);
    }));
    return Object.entries(acc)
      .map(([k, v]) => ({ k, avg: v.reduce((a, b) => a + b, 0) / v.length }))
      .filter(x => metricInfo[x.k])
      .sort((a, b) => a.avg - b.avg)
      .slice(0, 6);
  }, [data.employees, metricInfo]);

  const ranking = useMemo(() => data.employees
    .filter(e => e.result.complete)
    .sort((a, b) => b.result.score - a.result.score), [data.employees]);

  const factKeys = useMemo(() => {
    if (!series) return [];
    const keys = new Set();
    series.forEach(m => Object.keys(m.facts).forEach(k => keys.add(k)));
    return [...keys].filter(k => metricInfo[k]);
  }, [series, metricInfo]);

  useEffect(() => {
    if (!metric && factKeys.length) setMetric(factKeys.includes('managers.turnover') ? 'managers.turnover' : factKeys[0]);
  }, [factKeys, metric]);

  if (error) return <div className="ui-inline-error">{error}</div>;
  if (!series) return <div className="kpi2-empty">Собираем динамику…</div>;

  const labels = series.map(m => `${MONTHS_SHORT[m.month - 1]} ${String(m.year).slice(2)}`);
  const fundData = {
    labels,
    datasets: [{
      label: 'Фонд премий',
      data: series.map(m => m.fund),
      // canvas не понимает color-mix(): бледный столбец — тот же hex с альфой.
      backgroundColor: series.map(m => (m.status === 'closed' ? token('--c-chart-1') : `${token('--c-chart-1')}66`)),
      borderRadius: 6,
      maxBarThickness: 40,
    }],
  };
  const fundOptions = {
    responsive: true,
    maintainAspectRatio: false,
    plugins: {
      legend: { display: false },
      tooltip: {
        callbacks: {
          label: (ctx) => `${money(ctx.parsed.y)}`,
          afterLabel: (ctx) => {
            const m = series[ctx.dataIndex];
            return `премию получили ${m.with_bonus} из ${m.employees}${m.status === 'closed' ? '' : ' · черновик'}`;
          },
        },
      },
    },
    scales: {
      y: {
        beginAtZero: true,
        suggestedMax: 10000,
        grid: axisGrid(),
        ticks: { callback: (v) => (v >= 1000 ? `${Math.round(v / 1000)} тыс.` : `${v} ₽`) },
      },
      x: { grid: { display: false } },
    },
  };

  const info = metricInfo[metric];
  const factSeries = series.map(m => m.facts[metric] || null);
  const factData = info && {
    labels,
    datasets: [
      {
        label: 'Факт',
        data: factSeries.map(f => f?.value ?? null),
        borderColor: token('--c-chart-1'),
        backgroundColor: token('--c-chart-1'),
        borderWidth: 3,
        tension: 0.25,
        pointRadius: 4,
        spanGaps: true,
      },
      {
        label: 'Цель (10 баллов)',
        data: factSeries.map(f => f?.goal ?? null),
        borderColor: token('--c-chart-2'),
        borderDash: [6, 4],
        borderWidth: 2,
        // одна точка линией не рисуется — пока месяц один, показываем маркер
        pointRadius: series.length < 2 ? 5 : 0,
        pointStyle: 'line',
        pointBorderWidth: 3,
        stepped: 'middle',
      },
      {
        label: 'Порог премии (7 баллов)',
        data: factSeries.map(f => f?.bonus_level ?? null),
        borderColor: token('--c-chart-3'),
        borderDash: [2, 4],
        borderWidth: 2,
        // одна точка линией не рисуется — пока месяц один, показываем маркер
        pointRadius: series.length < 2 ? 5 : 0,
        pointStyle: 'line',
        pointBorderWidth: 3,
        stepped: 'middle',
      },
    ],
  };
  const factOptions = info && {
    responsive: true,
    maintainAspectRatio: false,
    interaction: { mode: 'index', intersect: false },
    plugins: {
      legend: { position: 'bottom', labels: { usePointStyle: true, boxWidth: 8, padding: 14 } },
      tooltip: { callbacks: { label: (ctx) => `${ctx.dataset.label}: ${fmtValue(info.unit, ctx.parsed.y)}` } },
    },
    scales: {
      y: { grid: axisGrid(), ticks: { callback: (v) => fmtValue(info.unit, v) } },
      x: { grid: { display: false } },
    },
  };

  const rolesWithData = data.roles.filter(r => series.some(m => m.roles[r.key] !== undefined));

  return (
    <div className="kpi2-analytics">
      {series.length < 2 && (
        <div className="kpi2-note">
          Пока посчитан только один месяц. Графики динамики наполнятся, когда появятся следующие месяцы, — цели
          каждого месяца сохраняются отдельно, поэтому сравнение будет честным.
        </div>
      )}

      <div className="kpi2-analytics-grid">
        <section className="kpi2-card">
          <div className="kpi2-card-head">
            <h3>Фонд премий по месяцам</h3>
            <p>Яркие столбцы — закрытые месяцы, бледные — черновики.</p>
          </div>
          <div className="kpi2-chart" role="img" aria-label="Фонд премий по месяцам">
            <Bar data={fundData} options={fundOptions} />
          </div>
        </section>

        <section className="kpi2-card">
          <div className="kpi2-card-head">
            <h3>Слабые места месяца</h3>
            <p>
              Показатели с самым низким средним баллом — на них и стоит направить разбор с командой.
              {data.status !== 'closed' && ' Месяц ещё идёт: суммы за месяц (выручка, продажи) пока набираются.'}
            </p>
          </div>
          {weak.length === 0 ? <div className="kpi2-muted">Нет посчитанных показателей</div> : (
            <ul className="kpi2-bars">
              {weak.map(w => (
                <li key={w.k}>
                  <span className="kpi2-bars-label">
                    {metricInfo[w.k].name}<span className="kpi2-muted"> · {metricInfo[w.k].roleName}</span>
                  </span>
                  <span className="kpi2-bars-track">
                    <span className={`kpi2-bars-fill tone-${scoreTone(w.avg)}`} style={{ width: `${w.avg * 10}%` }} />
                  </span>
                  <span className="kpi2-bars-value">{fmtScore(w.avg)}</span>
                </li>
              ))}
            </ul>
          )}
        </section>
      </div>

      <section className="kpi2-card">
        <div className="kpi2-card-head kpi2-card-head-row">
          <div>
            <h3>Факт против цели</h3>
            <p>Как показатель держится относительно цели и порога премии из месяца в месяц.</p>
          </div>
          <select value={metric} onChange={e => setMetric(e.target.value)} aria-label="Показатель">
            {factKeys.map(k => (
              <option key={k} value={k}>{metricInfo[k].roleName}: {metricInfo[k].name}</option>
            ))}
          </select>
        </div>
        {factData ? (
          <div className="kpi2-chart" role="img" aria-label={`Факт и цель: ${info.name}`}>
            <Line data={factData} options={factOptions} />
          </div>
        ) : <div className="kpi2-muted">Нет общих показателей с фактом</div>}
      </section>

      <div className="kpi2-analytics-grid">
        <section className="kpi2-card">
          <div className="kpi2-card-head">
            <h3>Средний балл по должностям</h3>
            <p>Цвет — уровень: зелёный от 9, синий от 7 (есть премия), жёлтый 5–7, красный ниже 5.</p>
          </div>
          <div className="kpi2-scale-wrap">
            <table className="kpi2-heat">
              <thead>
                <tr><th>Должность</th>{labels.map(l => <th key={l}>{l}</th>)}</tr>
              </thead>
              <tbody>
                {rolesWithData.map(r => (
                  <tr key={r.key}>
                    <th scope="row">{roleName[r.key]}</th>
                    {series.map(m => {
                      const v = m.roles[r.key];
                      return <td key={`${m.year}-${m.month}`} className={`tone-${scoreTone(v)}`}>{fmtScore(v)}</td>;
                    })}
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </section>

        <section className="kpi2-card">
          <div className="kpi2-card-head">
            <h3>Рейтинг сотрудников</h3>
            <p>Итоговый балл за выбранный месяц.</p>
          </div>
          {ranking.length === 0 ? <div className="kpi2-muted">Нет посчитанных сотрудников</div> : (
            <ul className="kpi2-bars">
              {ranking.map((e, i) => (
                <li key={e.id}>
                  <span className="kpi2-bars-label">
                    {i + 1}. {e.name}<span className="kpi2-muted"> · {roleName[e.role]}</span>
                  </span>
                  <span className="kpi2-bars-track">
                    <span className={`kpi2-bars-fill tone-${scoreTone(e.result.score)}`} style={{ width: `${e.result.score * 10}%` }} />
                  </span>
                  <span className="kpi2-bars-value">{fmtScore(e.result.score)}</span>
                </li>
              ))}
            </ul>
          )}
        </section>
      </div>
    </div>
  );
};

export default KpiAnalytics;
